import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import Heading from "atoms/Heading";
import Text from "atoms/Text";
import Button from "atoms/Button";
import CheckBox from "atoms/CheckBox";
import OtpComponent from "../molecules/OtpComponent";
import { Controller } from "react-hook-form";
import { getFeatureFlags } from "lib/utils";
import { otpResendTimeInSec, otpLength } from "../constants";

const CandidateOtpScreen = ({
  handleLogin,
  loginDetails,
  resendOtp,
  error,
  errorStatus,
  control,
  setValue,
  isAdmin,
  theme,
}) => {
  const flag = getFeatureFlags();
  const [otp, setOtp] = useState("");
  const isRateLimitError = errorStatus === 429;
  //timer logic
  const [timer, setTimer] = useState(otpResendTimeInSec);
  const timeOutCallback = useCallback(
    () => setTimer((currTimer) => currTimer - 1),
    []
  );
  useEffect(() => {
    let timeoutId;
    if (timer > 0) {
      timeoutId = setTimeout(timeOutCallback, 1000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timer, timeOutCallback]);

  const resetTimer = function () {
    if (!timer) {
      setTimer(otpResendTimeInSec);
    }
  };

  //events
  const onClickLogin = (event) => {
    event.preventDefault();
    handleLogin(otp);
  };
  const handleResendOtp = () => {
    resetTimer();
    resendOtp();
  };


  // Custom styles for OTP inputs when theme is applied
  const otpCustomStyles = theme?.template ? {
    inputStyle: {
      backgroundColor: "transparent",
      color: "var(--login-right-text)",
      border: "1px solid var(--login-right-text)",
    },
    focusStyle: {
      backgroundColor: "transparent",
      color: "var(--login-right-text)",
      border: "1px solid var(--login-right-text)",
    },
  } : {};

  return (
    <div className="w-full h-full flex items-center justify-center">
      <form onSubmit={onClickLogin}>
        <Heading
          type="h4"
          className="mb-2 md:mb-1 text-32px text-center font-semibold text-[color:var(--login-right-heading)]"
        >
          Enter verification code
        </Heading>
        <div className="mb-8 md:mb-9">
          <Text className="text-center text-[14px] md:tracking-widest text-[color:var(--login-right-text)] opacity-90">
            {timer === 0 && !isRateLimitError ? (
              <>
                Didn't get the OTP? Please check if your{" "}
                {loginDetails.loginType === "email"
                  ? "email address"
                  : "mobile number"}
                <br />
                <span className="font-semibold break-all inline-block max-w-full text-[color:var(--login-right-text)]">
                  {loginDetails.loginType === "email"
                    ? loginDetails.emailId
                    : loginDetails.mobileNumber}
                </span>{" "}
                is correct.
              </>
            ) : (
              <>
                If the provided{" "}
                <span className="font-semibold ml-1 tracking-normal text-[color:var(--login-right-text)]">
                  {loginDetails.loginType === "email"
                    ? "email address"
                    : "mobile number"}
                </span>
                &nbsp; is registered with us, you will receive an{" "}
                <span className="font-semibold ml-1 tracking-normal text-[color:var(--login-right-text)]">
                  {loginDetails.loginType === "email" ? "email" : "SMS"}
                </span>
                &nbsp; with an OTP.
              </>
            )}
          </Text>
        </div>
        <div className="flex justify-center">
          <OtpComponent
            onChange={setOtp}
            otpValue={otp}
            length={otpLength}
            errorMessage={isRateLimitError ? "" : error}
            customStyles={otpCustomStyles}
          />
        </div>

        {isRateLimitError && (
          <Text className="my-8 md:my-4 text-center text-sm text-error font-light px-2">
            {error}
          </Text>
        )}

        {timer > 0 && !isRateLimitError && (
          <Text className="my-8 md:my-4 md:mt-4 md:mb-4 text-center md:-mb-6 text-sm text-[color:var(--login-right-text)] opacity-80 font-light">
            Resend OTP in{" "}
            <span className="font-bold text-[color:var(--login-right-text)]">
              {timer}s
            </span>
          </Text>
        )}
        {isAdmin ? (
          <Button
            variant="blackBtn"
            fontWeight="font-bold"
            btnClass="w-full mt-10"
            disabled={otp.length != otpLength}
            type="submit"
          >
            Login
          </Button>
        ) : (
          <Button
            type="submit"
            variant="normalBtn"
            fontWeight="font-bold"
            btnClass="w-full md:mt-10 border border-transparent disabled:border-transparent"
            disabled={otp.length !== otpLength}
            style={{
              background:
                otp.length !== otpLength
                  ? "var(--login-button-disabled)"
                  : "var(--login-button)",
              color:
                otp.length !== otpLength
                  ? "var(--login-button-disabled-text)"
                  : "var(--login-button-text)",
            }}
          >
            Login
          </Button>
        )}
        {timer === 0 && !isRateLimitError && (
          <div onClick={handleResendOtp}>
            <Text className="text-center my-8 md:my-4 cursor-pointer text-[color:var(--login-right-text)] opacity-80">
              Send the code again
            </Text>
          </div>
        )}
        {!loginDetails.whatsappConsent &&
          flag?.whatsapp_consent?.enabled &&
          !isAdmin && (
            <div className="my-5">
              <Controller
                control={control}
                name={`whatsappConsent`}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <CheckBox
                    name="whatsappConsent"
                    checkBoxLabel="Send Notification via Whatsapp"
                    isSelected={value}
                    value={value}
                    onChange={(e) => {
                      setValue("whatsappConsent", e.target.checked);
                    }}
                  />
                )}
              />
            </div>
          )}
      </form>
    </div>
  );
};
export default CandidateOtpScreen;

CandidateOtpScreen.propTypes = {
  loginDetails: PropTypes.object,
  error: PropTypes.string,
  errorStatus: PropTypes.number,
  handleLogin: PropTypes.func,
  resendOtp: PropTypes.func,
  theme: PropTypes.object,
};
